import React, { useState, useEffect } from 'react';
import { articleService } from '../config';

const ArticleSubmissionForm = ({ onSubmit, initialData = null, isEditing = false, publishers = [], authors = [], hideSubmitButton = false, formId = '' }) => {
  // State for form fields
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    publisher_id: '',
    author_id: '',
    publication_date: new Date().toISOString().split('T')[0],
    context: '', // Added for manual context input
    new_publisher_name: '',
    new_author_name: ''
  });
  
  // State for extraction process
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extractedContent, setExtractedContent] = useState('');
  const [error, setError] = useState(null);
  const [apiUrl, setApiUrl] = useState('');
  
  // State for manual content entry
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualContent, setManualContent] = useState('');
  
  // State for direct manual mode (skip extraction)
  const [directManualMode, setDirectManualMode] = useState(false);

  // Effect to initialize form with data if editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        url: initialData.url || '',
        title: initialData.title || '',
        publisher_id: initialData.publisher_id || '',
        author_id: initialData.author_id || '',
        publication_date: initialData.publication_date || new Date().toISOString().split('T')[0],
        context: initialData.context || '',
        new_publisher_name: initialData.new_publisher_name || '',
        new_author_name: initialData.new_author_name || ''
      });
      
      if (initialData.content) {
        setExtractedContent(initialData.content);
        setManualContent(initialData.content);
      }
    }
  }, [initialData]);
  
  // Get API URL for display
  useEffect(() => {
    // Use the current origin to avoid cross-origin issues
    const url = window.location.hostname === 'localhost' 
      ? 'http://localhost:8788' 
      : window.location.origin;
    setApiUrl(url);
    console.log('Using API URL:', url);
  }, []);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear any errors
    if (error) {
      setError(null);
    }
  };
  
  // Toggle direct manual mode
  const toggleDirectManualMode = () => {
    const newMode = !directManualMode;
    setDirectManualMode(newMode);
    
    // If enabling direct manual mode, show the manual entry field
    if (newMode) {
      setShowManualEntry(true);
    }
  };
  
  // Extract article content
  const extractArticleContent = async () => {
    if (!formData.url) {
      setError('Please enter a URL');
      return;
    }
    
    try {
      setIsExtracting(true);
      setError(null);
      setShowManualEntry(false);
      
      console.log('Extracting article content from:', formData.url);
      const data = await articleService.extractArticle(formData.url);
      
      // Update form with extracted data
      setFormData(prev => ({
        ...prev,
        title: data.title || prev.title,
        publication_date: data.publication_date || prev.publication_date,
      }));
      
      // Find matching publisher or create new one
      if (data.publisher && publishers.length > 0) {
        const matchedPublisher = publishers.find(p => 
          p.name.toLowerCase() === data.publisher.toLowerCase());
        
        if (matchedPublisher) {
          setFormData(prev => ({ ...prev, publisher_id: matchedPublisher.id }));
        }
      }
      
      // Find matching author or create new one
      if (data.author && authors.length > 0) {
        const matchedAuthor = authors.find(a => 
          a.name.toLowerCase() === data.author.toLowerCase());
        
        if (matchedAuthor) {
          setFormData(prev => ({ ...prev, author_id: matchedAuthor.id }));
        }
      }
      
      // Set extracted content
      setExtractedContent(data.content.text);
      
    } catch (err) {
      console.error('Extraction error:', err);
      setError(err.message);
      
      // If we get a 403 Forbidden error, show manual entry option
      if (err.message && (
          err.message.includes('403') || 
          err.message.includes('Access forbidden') || 
          err.message.includes('blocking our extraction tool')
        )) {
        setShowManualEntry(true);
      }
    } finally {
      setIsExtracting(false);
    }
  };
  
  // Handle manual content input
  const handleManualContentChange = (e) => {
    const newContent = e.target.value;
    setManualContent(newContent);
    
    // Update extracted content in real-time as user types
    if (newContent.trim()) {
      setExtractedContent(newContent);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.url || !formData.title) {
      setError('URL and title are required');
      return;
    }
    
    // Validate publisher
    if (formData.publisher_id === 'new_publisher' && !formData.new_publisher_name.trim()) {
      setError('Please enter a name for the new publisher');
      return;
    }
    
    // Validate author
    if (formData.author_id === 'new_author' && !formData.new_author_name.trim()) {
      setError('Please enter a name for the new author');
      return;
    }
    
    // For direct manual mode, use the manual content as extracted content
    if (directManualMode) {
      if (!manualContent || manualContent.trim().length < 20) {
        setError('Please enter article content (minimum 20 characters)');
        return;
      }
      // Update extracted content with manual content
      setExtractedContent(manualContent);
    } else {
      // Validate content for non-manual mode
      if (!extractedContent || extractedContent.trim().length < 20) {
        setError('Please extract or manually enter article content');
        return;
      }
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Get the final content (either from extraction or manual entry)
      const finalContent = directManualMode ? manualContent : extractedContent;
      
      // Prepare submission data
      const submissionData = {
        ...formData,
        content: finalContent,
        // Add flags to indicate new entities need to be created
        create_new_publisher: formData.publisher_id === 'new_publisher',
        create_new_author: formData.author_id === 'new_author',
        // Add flag to indicate manual entry was used
        manual_entry: directManualMode || showManualEntry,
        // Flag to indicate this article should remain unpublished until complete
        status: 'Draft'
      };
      
      // Call the parent's onSubmit handler
      const result = await onSubmit(submissionData);
      
      // If successful and we receive an article ID, handle next step based on whether we're in a workflow
      if (result && result.id && !isEditing) {
        // Check if we're being used inside the unified workflow
        if (onSubmit.onContinue) {
          // If we're in the unified workflow, just continue to the next step without prompting
          onSubmit.onContinue(result, finalContent);
          return result;
        } else {
          // Only show confirmation dialog if not in the unified workflow
          const shouldExtract = window.confirm(
            "Article saved successfully! Would you like to extract claims from this article now?"
          );
          
          if (shouldExtract) {
            // For backward compatibility with old flow
            if (onSubmit.onContinue) {
              onSubmit.onContinue(result, finalContent);
            }
            return result;
          }
        }
      }
      
      // Reset form if not editing and not continuing to next step
      if (!isEditing) {
        setFormData({
          url: '',
          title: '',
          publisher_id: '',
          author_id: '',
          publication_date: new Date().toISOString().split('T')[0],
          context: '',
          new_publisher_name: '',
          new_author_name: ''
        });
        setExtractedContent('');
        setManualContent('');
        setShowManualEntry(false);
        setDirectManualMode(false);
      }
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 mb-4">
          <p className="text-sm font-medium">{error}</p>
          {error.includes('403') || error.includes('Access forbidden') || error.includes('blocking our extraction tool') ? (
            <p className="text-sm mt-2">
              Some websites block our extraction tool. You can manually copy and paste the article content below.
            </p>
          ) : null}
        </div>
      )}
      
      <div className="px-6 pt-4 text-xs text-gray-500">
        Using API URL: {apiUrl}
      </div>
      
      <form onSubmit={handleSubmit} className="p-6 space-y-6" id={formId || undefined}>
        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="url" className="block text-sm font-medium text-gray-700">
              Article URL
            </label>
            <div className="flex items-center">
              <input
                id="direct-manual-mode"
                name="direct-manual-mode"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={directManualMode}
                onChange={toggleDirectManualMode}
              />
              <label htmlFor="direct-manual-mode" className="ml-2 text-sm text-gray-600">
                Skip extraction (manual entry)
              </label>
            </div>
          </div>
          <div className="flex rounded-md shadow-sm">
            <input
              type="url"
              id="url"
              name="url"
              className="flex-1 min-w-0 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://example.com/article"
              value={formData.url}
              onChange={handleChange}
              required
            />
            {!directManualMode && (
              <button
                type="button"
                className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={extractArticleContent}
                disabled={isExtracting || !formData.url}
              >
                {isExtracting ? 'Extracting...' : 'Extract Content'}
              </button>
            )}
          </div>
        </div>
        
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Article Title
          </label>
          <input
            type="text"
            id="title"
            name="title"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="publisher_id" className="block text-sm font-medium text-gray-700 mb-1">
              Publisher
            </label>
            
            <div className="flex flex-col space-y-2">
              <select
                id="publisher_id"
                name="publisher_id"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={formData.publisher_id}
                onChange={handleChange}
              >
                <option value="">Select a publisher</option>
                {publishers.map(publisher => (
                  <option key={publisher.id} value={publisher.id}>
                    {publisher.name}
                  </option>
                ))}
                <option value="new_publisher">+ Add new publisher</option>
              </select>
              
              {formData.publisher_id === 'new_publisher' && (
                <div className="mt-2">
                  <label htmlFor="new_publisher_name" className="sr-only">New Publisher Name</label>
                  <input
                    type="text"
                    id="new_publisher_name"
                    name="new_publisher_name"
                    placeholder="Enter new publisher name"
                    className="block w-full px-3 py-2 border border-blue-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-blue-50"
                    value={formData.new_publisher_name}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label htmlFor="author_id" className="block text-sm font-medium text-gray-700 mb-1">
              Author
            </label>
            
            <div className="flex flex-col space-y-2">
              <select
                id="author_id"
                name="author_id"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={formData.author_id}
                onChange={handleChange}
              >
                <option value="">Select an author</option>
                {authors.map(author => (
                  <option key={author.id} value={author.id}>
                    {author.name}
                  </option>
                ))}
                <option value="new_author">+ Add new author</option>
              </select>
              
              {formData.author_id === 'new_author' && (
                <div className="mt-2">
                  <label htmlFor="new_author_name" className="sr-only">New Author Name</label>
                  <input
                    type="text"
                    id="new_author_name"
                    name="new_author_name"
                    placeholder="Enter new author name"
                    className="block w-full px-3 py-2 border border-blue-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-blue-50"
                    value={formData.new_author_name}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <label htmlFor="publication_date" className="block text-sm font-medium text-gray-700 mb-1">
            Publication Date
          </label>
          <input
            type="date"
            id="publication_date"
            name="publication_date"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={formData.publication_date}
            onChange={handleChange}
          />
        </div>
        
        <div>
          <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-1">
            Context (optional)
          </label>
          <textarea
            id="context"
            name="context"
            rows={3}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add context about this article to help Claude with analysis..."
            value={formData.context}
            onChange={handleChange}
          ></textarea>
          <p className="mt-1 text-xs text-gray-500">
            Provide additional context about the article that might improve Claude's analysis.
          </p>
        </div>
        
        {(showManualEntry || directManualMode) && (
          <div className={`p-4 rounded-md border mb-4 ${directManualMode ? 'bg-blue-50 border-blue-300' : 'bg-yellow-50 border-yellow-300'}`}>
            <h3 className={`font-medium ${directManualMode ? 'text-blue-800' : 'text-yellow-800'}`}>
              {directManualMode ? 'Manual Content Entry' : 'Website Extraction Blocked'}
            </h3>
            <p className={`text-sm mt-1 ${directManualMode ? 'text-blue-700' : 'text-yellow-700'}`}>
              {directManualMode 
                ? 'Enter the article content manually below.' 
                : 'We couldn\'t automatically extract content from this website. Please manually copy and paste the article content below.'}
            </p>
            <div className="mt-3">
              <textarea
                rows={10}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${directManualMode ? 'border-blue-300' : 'border-yellow-300'}`}
                placeholder="Paste the article content here..."
                value={manualContent}
                onChange={handleManualContentChange}
                onBlur={() => {
                  if (manualContent.trim()) {
                    setExtractedContent(manualContent);
                  }
                }}
              ></textarea>
            </div>
          </div>
        )}
        
        {(extractedContent || (directManualMode && manualContent)) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {directManualMode || showManualEntry ? 'Content Preview' : 'Extracted Content'}
            </label>
            <div className="mt-1 p-3 border border-gray-300 rounded-md bg-gray-50 max-h-60 overflow-y-auto">
              <p className="whitespace-pre-wrap">
                {(directManualMode ? manualContent : extractedContent).substring(0, 500)}
                {(directManualMode ? manualContent : extractedContent).length > 500 && '...'}
              </p>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {(directManualMode ? manualContent : extractedContent).length} characters
              {!directManualMode && !showManualEntry ? ' extracted' : ''}. 
              {(directManualMode ? manualContent : extractedContent).split(/\s+/).length} words.
            </p>
          </div>
        )}
        
        {!hideSubmitButton && (
          <div className="flex justify-end space-x-3 pt-4">
            {isEditing && (
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => window.history.back()}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
              id="article-submit-button"
            >
              {isSubmitting ? 'Processing...' : isEditing ? 'Update Article' : 'Save & Continue'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default ArticleSubmissionForm;